// src/app/modules/.../SatisfactionFeedback.tsx
import React, {FC, useEffect, useMemo, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Button} from '../../../../components/Button/Button'
import {Loading} from '../../../../components/Loading/Loading'
import {ServiceRequestModal} from '../../../../components/ServiceResquests/Modal'
import styles from './satisfactionFeedback.module.css'
import {Input} from '../../../../components/Input/Input'
import {GridSelectionModal} from './components/GridSelectionModal'
import useModals from '../../../../hooks/useModals'
import {GET, POST} from '../../../../services/api'
import SVG from 'react-inlinesvg'
import {EditorQuill} from '../../../../components/EditorQuill/EditorQuill'
import {i18n} from '../../../../translate/i18n'
import Swal from 'sweetalert2'
import {useNavigate} from 'react-router-dom'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'

// DnD-kit
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import uuid from 'react-uuid'

// Catálogo dos componentes de feedback de satisfação
import {
  FORMS_CATALOG_EXTRA,
  FormBlockKindExtra,
  FormBlockMeta,
  TitleBox,
  PreviewLock,
} from '../../../../components/SatisfactionFeedback'

// ---------------- Tipos/Helpers ----------------
type Block = {id: string; kind: FormBlockKindExtra; gridIds?: number[]; customTitle?: string}
const metaByKind = (k: FormBlockKindExtra) => FORMS_CATALOG_EXTRA.find((m) => m.id === k)!

type GridHotelFeedback = {
  id: number
  title: string
  status: boolean
  content: {
    config: string[]
  }
}

// Item sortável (bloco no canvas)
function SortableBlock({
  block,
  meta,
  onRemove,
  onOpenGridModal,
  onConfigureTitle,
}: {
  block: Block
  meta: FormBlockMeta
  onRemove: (id: string) => void
  onOpenGridModal: (blockId: string) => void
  onConfigureTitle: (blockId: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({id: block.id})

  return (
    <div
      ref={setNodeRef}
      className={`${styles.item} ${isDragging ? styles.dragging : ''}`}
      style={{transform: CSS.Transform.toString(transform), transition}}
      aria-roledescription='sortable item'
    >
      <div className={styles.header}>
        {/* HANDLE — só ele arrasta */}
        <button
          type='button'
          className={styles.handle}
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          aria-label={i18n.t('satisfaction_feedback.drag_title')}
          title={i18n.t('satisfaction_feedback.drag_action')}
        >
          <i className='bi bi-grip-vertical' />
        </button>

        {meta.icon && <i className={`${meta.icon}`} />}
        <span className={styles.title}>
          {meta.title}
          {block.gridIds && block.gridIds.length > 0 && (
            <span className='badge badge-light-primary ms-2 fs-8'>
              {block.gridIds.length}{' '}
              {block.gridIds.length === 1
                ? i18n.t('satisfaction_feedback.grid_selected')
                : i18n.t('satisfaction_feedback.grids_selected')}
            </span>
          )}
        </span>

        <span className={styles.spacer} />

        {/* Botão de Configurar Título - aparece apenas para tipo "title" */}
        {block.kind === 'title' && (
          <button
            type='button'
            className={styles.iconBtn}
            onClick={() => onConfigureTitle(block.id)}
            title={i18n.t('satisfaction_feedback.configure_title')}
            aria-label={i18n.t('satisfaction_feedback.configure_title')}
          >
            <i className='bi bi-pencil' />
          </button>
        )}

        {/* Botão de Grades de Feedback - aparece para tipos "checkbox", "radio" e "select" */}
        {(block.kind === 'checkbox' || block.kind === 'radio' || block.kind === 'select') && (
          <button
            type='button'
            className={styles.iconBtn}
            onClick={() => onOpenGridModal(block.id)}
            title={i18n.t('satisfaction_feedback.grid_feedback')}
            aria-label={i18n.t('satisfaction_feedback.grid_feedback')}
          >
            <i className='bi bi-grid' />
          </button>
        )}

        <button
          type='button'
          className={styles.iconBtn}
          onClick={() => onRemove(block.id)}
          title={i18n.t('satisfaction_feedback.remove_title')}
          aria-label={i18n.t('satisfaction_feedback.remove_aria_label')}
        >
          <i className='bi bi-trash text-danger' />
        </button>
      </div>

      {/* Preview só visual e compacto */}
      <div className={styles.body}>
        {block.kind === 'title' && block.customTitle ? (
          <PreviewLock>
            <TitleBox value={block.customTitle} />
          </PreviewLock>
        ) : (
          meta.getPreview()
        )}
      </div>
    </div>
  )
}

// ---------------- Página ----------------
const Content: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [openOptionsMenu, setOpenOptionsMenu] = useState(false)

  const [blocks, setBlocks] = useState<Block[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)

  // Campos da sessão (topo)
  const [sessionTitle, setSessionTitle] = useState<string>('') // título
  const [status, setStatus] = useState<boolean>(true) // ativo/inativo

  const [description, setDescription] = useState<string>('')

  // Campos de vigência do formulário
  const [dateStart, setDateStart] = useState<string>('')
  const [dateEnd, setDateEnd] = useState<string>('')

  // Estados para modal de grades de feedback
  const [openGridModal, setOpenGridModal] = useState(false)
  const [currentBlockId, setCurrentBlockId] = useState<string | null>(null)
  const [gridHotelFeedbacks, setGridHotelFeedbacks] = useState<GridHotelFeedback[]>([])
  const [loadingGrids, setLoadingGrids] = useState(false)
  const [gridSearchQuery, setGridSearchQuery] = useState('')
  const [selectedGridIds, setSelectedGridIds] = useState<number[]>([])

  // Estados para configuração de título
  const [openTitleModal, setOpenTitleModal] = useState(false)
  const [currentTitleBlockId, setCurrentTitleBlockId] = useState<string | null>(null)
  const [customTitleInput, setCustomTitleInput] = useState<string>('')

  const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 6}}))

  const addBlock = (kind: FormBlockKindExtra) => setBlocks((prev) => [...prev, {id: uuid(), kind}])
  const removeBlock = (id: string) => setBlocks((prev) => prev.filter((b) => b.id !== id))

  // Função para abrir modal de grades de feedback
  const openGridModalForBlock = (blockId: string) => {
    setCurrentBlockId(blockId)
    setOpenGridModal(true)
    setGridSearchQuery('')

    // Carregar grades já selecionadas para este bloco
    const currentBlock = blocks.find((b) => b.id === blockId)
    setSelectedGridIds(currentBlock?.gridIds || [])

    fetchGridHotelFeedbacks()
  }

  // Função para buscar grades de feedback
  const fetchGridHotelFeedbacks = async () => {
    try {
      if (gridHotelFeedbacks.length !== 0) return
      setLoadingGrids(true)
      const res = await GET('/grid-hotel-feedback')
      setGridHotelFeedbacks(res.data || [])
    } catch (error) {
      console.error(i18n.t('satisfaction_feedback.grid_feedback_error'), error)
      setGridHotelFeedbacks([])
    } finally {
      setLoadingGrids(false)
    }
  }

  // Função para togglear seleção de uma grade
  const toggleGridSelection = (gridId: number) => {
    setSelectedGridIds((prev) => {
      if (prev.includes(gridId)) {
        return prev.filter((id) => id !== gridId)
      } else {
        return [...prev, gridId]
      }
    })
  }

  // Função para confirmar seleção de grades
  const confirmGridSelection = () => {
    if (currentBlockId) {
      setBlocks((prev) =>
        prev.map((block) =>
          block.id === currentBlockId ? {...block, gridIds: selectedGridIds} : block
        )
      )
    }
    setOpenGridModal(false)
    setCurrentBlockId(null)
    setSelectedGridIds([])
    setGridSearchQuery('')
  }

  // Função para abrir modal de configuração de título
  const openTitleModalForBlock = (blockId: string) => {
    setCurrentTitleBlockId(blockId)
    const currentBlock = blocks.find((b) => b.id === blockId)
    setCustomTitleInput(currentBlock?.customTitle || '')
    setOpenTitleModal(true)
  }

  // Função para confirmar título personalizado
  const confirmCustomTitle = () => {
    if (currentTitleBlockId && customTitleInput.trim()) {
      setBlocks((prev) =>
        prev.map((block) =>
          block.id === currentTitleBlockId
            ? {...block, customTitle: customTitleInput.trim()}
            : block
        )
      )
    }
    setOpenTitleModal(false)
    setCurrentTitleBlockId(null)
    setCustomTitleInput('')
  }

  const onDragEnd = ({active, over}: DragEndEvent) => {
    setActiveId(null)
    if (!over || active.id === over.id) return
    setBlocks((prev) => {
      const oldIndex = prev.findIndex((b) => b.id === active.id)
      const newIndex = prev.findIndex((b) => b.id === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const activeBlock = activeId ? blocks.find((b) => b.id === activeId) : null

  // Filtrar grades por busca
  const filteredGrids = useMemo(() => {
    if (!gridSearchQuery) return gridHotelFeedbacks
    const query = gridSearchQuery.toLowerCase()
    return gridHotelFeedbacks.filter((grid) => grid.title.toLowerCase().includes(query))
  }, [gridHotelFeedbacks, gridSearchQuery])

  const ensureDescription = (html: string) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const text = doc.body.textContent?.trim() || ''

    return text === '' ? '' : html.trim()
  }

  // Função para salvar o formulário de feedback de satisfação
  const handleSave = async () => {
    try {
      setLoading(true)

      // Validação básica
      if (!sessionTitle.trim()) {
        throw new Error(String(i18n.t('satisfaction_feedback.validation_title')))
      }

      // Se uma data estiver preenchida e a outra não, não gera erro
      let startDate: Date | null = null
      let endDate: Date | null = null

      if (dateStart.trim()) {
        startDate = new Date(`${dateStart}T00:00:00`)
      }

      if (dateEnd.trim()) {
        endDate = new Date(`${dateEnd}T23:59:59.999`)
      }

      // Se ambas existirem, validar o intervalo
      if (startDate && endDate && startDate >= endDate) {
        throw new Error(String(i18n.t('satisfaction_feedback.validation_date_range')))
      }

      if (blocks.length === 0) {
        throw new Error(String(i18n.t('satisfaction_feedback.validation_components')))
      }

      // Validação específica para "title"
      const titleBlocksWithoutCustomTitle = blocks.filter(
        (block) => block.kind === 'title' && (!block.customTitle || !block.customTitle.trim())
      )

      if (titleBlocksWithoutCustomTitle.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('satisfaction_feedback.validation_title_required')),
          text: String(i18n.t('satisfaction_feedback.validation_title_text')),
          confirmButtonText: 'OK',
        })
        return
      }

      // Validação específica para "checkbox", "radio" e "select"
      const gridBlocksWithoutGrids = blocks.filter(
        (block) =>
          (block.kind === 'checkbox' || block.kind === 'radio' || block.kind === 'select') &&
          (!block.gridIds || block.gridIds.length === 0)
      )

      if (gridBlocksWithoutGrids.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('satisfaction_feedback.validation_grids_required')),
          text: String(i18n.t('satisfaction_feedback.validation_grids_text')),
          confirmButtonText: 'OK',
        })
        return
      }

      // Montagem do payload
      const payload = {
        title: sessionTitle.trim(),
        description: ensureDescription(description),
        status,
        dateStart: startDate ? startDate : null,
        dateEnd: endDate ? endDate : null,
        content: {
          config: blocks.map(({id, kind, gridIds, customTitle, ...rest}, index) => ({
            order: index,
            type: kind,
            settings: {
              // campos comuns
              ...(gridIds && gridIds.length > 0 && {gridHotelFeedbackIds: gridIds}),
              // campos específicos
              ...(kind === 'title' && customTitle && {customTitle: customTitle.trim()}),
              // caso existam outros dados no block, mantenha-os sob settings também
              ...(Object.keys(rest).length > 0 && {extras: rest}),
            },
          })),
        },
      }

      const res = await POST('/feedbackform', payload)

      if (res.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('general.success')),
          text: String(i18n.t('satisfaction_feedback.success_created')),
        }).then(() => navigate('/hospitality/feedback-satisfaction/feedback-list'))
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('general.error')),
          text: res?.message || String(i18n.t('satisfaction_feedback.error_create')),
        })
      }
    } catch (error: any) {
      console.error('Erro ao salvar formulário de feedback de satisfação:', error)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('general.error')),
        text: error?.message || String(i18n.t('satisfaction_feedback.error_create')),
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('satisfaction_feedback.create')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('satisfaction_feedback.visual_instructions')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t('satisfaction_feedback.add_component')}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{type: 'button'}}
                  click={() => setOpenOptionsMenu(true)}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>

            <div className='card-body'>
              {/* ===== Topo: Título e Ícone (preview) ===== */}
              <div>
                <div className='row g-3 align-items-end'>
                  <div className='col-12'>
                    <div className={styles.configLabel}>
                      {i18n.t('satisfaction_feedback.form_title')}
                    </div>
                    <Input
                      inputAttr={{type: 'default'}}
                      label=''
                      value={sessionTitle}
                      change={(v: string) => setSessionTitle(v)}
                      placeholder={String(i18n.t('satisfaction_feedback.placeholder_title'))}
                      maxCaracteres={30}
                    />
                  </div>

                  <div className='col-2'>
                    <div className={styles.configLabel}>
                      {i18n.t('satisfaction_feedback.form_status')}
                    </div>
                    <Input
                      inputAttr={{type: 'select'}}
                      value={status}
                      change={(val: boolean) => setStatus(val)}
                      options={[
                        {select: i18n.t(`general.active`), value: true},
                        {select: i18n.t(`general.inactive`), value: false},
                      ]}
                      marginTop='0'
                    />
                  </div>

                  <div className='col-12'>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <div className={styles.configLabel}>
                          {i18n.t('satisfaction_feedback.form_date_init')}
                        </div>
                        <Input
                          inputAttr={{type: 'date'}}
                          label=''
                          value={dateStart}
                          change={(v: string) => setDateStart(v)}
                          placeholder={String(
                            i18n.t('satisfaction_feedback.placeholder_date_start')
                          )}
                        />
                      </div>
                      <div className='col-md-6'>
                        <div className={styles.configLabel}>
                          {i18n.t('satisfaction_feedback.form_date_end')}
                        </div>
                        <Input
                          inputAttr={{type: 'date'}}
                          label=''
                          value={dateEnd}
                          change={(v: string) => setDateEnd(v)}
                          placeholder={String(i18n.t('satisfaction_feedback.placeholder_date_end'))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* DESCRIÇÃO */}
                  <div className='col-12'>
                    <div className={styles.configLabel}>
                      {i18n.t('satisfaction_feedback.form_description')}
                    </div>
                    <div className='mt-3'>
                      <EditorQuill
                        change={(value: string) => setDescription(value)}
                        value={description}
                        maxLength={200}
                        height={120}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== Divisor das seções ===== */}
              <div className={styles.split}>
                <span className={styles.splitTitle}>
                  {i18n.t('satisfaction_feedback.sections')}
                </span>
              </div>

              {/* ===== Canvas / DnD ===== */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={(e) => setActiveId(String(e.active.id))}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={blocks.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className={styles.list}>
                    <div className='d-grid gap-3'>
                      {blocks.length === 0 ? (
                        <div className={styles.empty}>
                          {i18n.t('satisfaction_feedback.empty_message')}
                        </div>
                      ) : (
                        blocks.map((b) => (
                          <SortableBlock
                            key={b.id}
                            block={b}
                            meta={metaByKind(b.kind)}
                            onRemove={removeBlock}
                            onOpenGridModal={openGridModalForBlock}
                            onConfigureTitle={openTitleModalForBlock}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </SortableContext>

                <DragOverlay>
                  {activeBlock ? (
                    <div className={styles.overlay} style={{width: 640, maxWidth: '90vw'}}>
                      <SortableBlock
                        block={activeBlock}
                        meta={metaByKind(activeBlock.kind)}
                        onRemove={() => {}}
                        onOpenGridModal={() => {}}
                        onConfigureTitle={() => {}}
                      />
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>

            {/* Botões fixos no bottom da página */}
            <div className='card-footer border-0 pt-0 d-flex justify-content-between'>
              <Button
                text={i18n.t(`buttons.back`)}
                color='info'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={() => navigate('/hospitality/feedback-satisfaction/feedback-list')}
                btnClass='fw-bolder'
              />
              <Button
                text={i18n.t(`buttons.save`)}
                color='primary'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={handleSave}
                btnClass='fw-bolder'
              />
            </div>
          </div>

          {/* Modal “Adicionar componente” (catálogo) */}
          {openOptionsMenu && (
            <ServiceRequestModal
              onCloseAction={() => setOpenOptionsMenu(false)}
              title={i18n.t('satisfaction_feedback.add_component_modal')}
              showAlert={false}
            >
              <div className='pt-6 d-grid gap-2'>
                {FORMS_CATALOG_EXTRA.map((meta) => (
                  <button
                    key={meta.id}
                    className='btn btn-sm btn-light d-flex align-items-center text-start'
                    onClick={() => {
                      addBlock(meta.id)
                      setOpenOptionsMenu(false)
                    }}
                  >
                    {meta.icon && <i className={`${meta.icon} me-2`} />}
                    {meta.title}
                  </button>
                ))}
              </div>
            </ServiceRequestModal>
          )}

          {/* Modal de Configuração de Título */}
          {openTitleModal && (
            <ServiceRequestModal
              onCloseAction={() => {
                setOpenTitleModal(false)
                setCurrentTitleBlockId(null)
                setCustomTitleInput('')
              }}
              title={i18n.t('satisfaction_feedback.configure_title_modal')}
              width='400px'
            >
              <div className='d-flex flex-column gap-4 py-4'>
                <div>
                  <label className='form-label fw-bold required'>
                    {i18n.t('satisfaction_feedback.custom_title_label')}
                  </label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder={String(i18n.t('satisfaction_feedback.custom_title_placeholder'))}
                    value={customTitleInput}
                    onChange={(e) => setCustomTitleInput(e.target.value)}
                    maxLength={100}
                  />
                  <small className='text-muted'>
                    {i18n.t('satisfaction_feedback.custom_title_help')}
                  </small>
                </div>

                <div className='d-flex justify-content-end gap-2'>
                  <button
                    type='button'
                    className='btn btn-sm btn-danger'
                    onClick={() => {
                      setOpenTitleModal(false)
                      setCurrentTitleBlockId(null)
                      setCustomTitleInput('')
                    }}
                  >
                    {i18n.t('buttons.cancel')}
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-primary'
                    onClick={confirmCustomTitle}
                    disabled={!customTitleInput.trim()}
                  >
                    {i18n.t('buttons.save')}
                  </button>
                </div>
              </div>
            </ServiceRequestModal>
          )}

          {/* Modal de Grades de Feedback */}
          <GridSelectionModal
            isOpen={openGridModal}
            grids={gridHotelFeedbacks}
            loading={loadingGrids}
            searchQuery={gridSearchQuery}
            selectedGridIds={selectedGridIds}
            filteredGrids={filteredGrids}
            onSearchChange={setGridSearchQuery}
            onToggleGrid={toggleGridSelection}
            onClose={() => {
              setOpenGridModal(false)
              setCurrentBlockId(null)
              setSelectedGridIds([])
              setGridSearchQuery('')
            }}
            onConfirm={confirmGridSelection}
          />
        </div>
      )}
    </>
  )
}

const SatisfactionFeedbackCreate: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: i18n.t('satisfaction_feedback.breadcrumb_dashboard'),
            path: 'dashboard',
            isActive: false,
          },
          {
            title: i18n.t('satisfaction_feedback.title') as string,
            path: 'satisfaction-feedback',
            isActive: false,
          },
          {
            title: i18n.t('satisfaction_feedback.breadcrumb_create'),
            path: 'create',
            isActive: true,
          },
        ]}
      >
        {i18n.t('satisfaction_feedback.create')}
      </PageTitle>
      <Content />
    </>
  )
}

export {SatisfactionFeedbackCreate}
